import type { ExplorationHighlight, ViewMode } from '../../types/exploration';
import { isVolumeManaged } from '../../lib/classification';
import { clearSvgTextFill, readSvgTextFill, setSvgTextFill } from '../../lib/bubble/svgTextFill';
import { infraBubbleStyles as s, volumeBubbleStyles as vb } from './bubbleDimColors';
import { applyInfraGroupFilter, isInfraHierarchyType, type BubbleInfraContainer } from './bubbleInfraStyles';
import {
  isVolumeDomainType,
  type PackLayoutNode,
  type PackNode,
  type PackNodeData,
} from '../../lib/bubble/packNodeTypes';
import {
  BUBBLE_STROKE_WIDTH,
  fillForBubble,
  fillOpacityForBubble,
  strokeDashForBubble,
  strokeForBubble,
  strokeOpacityForBubble,
  strokeWidthForBubble,
  type BubbleDatum,
} from '../../lib/bubble/bubbleHelpers';
import type { ChipElement } from './bubbleDomTypes';

/** Emphasis / dim / chip-kind tokens used by bubble highlight DOM updates. */
export const bubbleEmphasisTokens = {
  /** CSS token (docs); DOM highlight uses `highlightStrokeResolved`. */
  highlightStroke: s.emphasis.highlightStroke,
  highlightStrokeResolved: s.emphasis.highlightStrokeResolved,
  highlightStrokeWidth: String(BUBBLE_STROKE_WIDTH),
  chipKindName: 'name' as const,
  chipKindSize: 'size' as const,
  dimmedFill: s.emphasis.dimmedFill,
  dimmedStroke: s.emphasis.dimmedStroke,
  dimElementOpacity: s.emphasis.elementOpacity,
  volumeEmphasisShadow: vb.volume.emphasis.shadowFilter,
} as const;

type VolumeShadowStateKey = 'sbHighlightFilter' | 'sbVolumeFilter';

/** Transient hover or persistent highlight/selection glow on volume leaf circles. */
export function setVolumeBubbleShadow(
  circle: SVGCircleElement,
  on: boolean,
  stateKey?: VolumeShadowStateKey
): void {
  if (on) {
    if (stateKey && circle.dataset[stateKey] == null) {
      circle.dataset[stateKey] = restingVolumeFilter(circle);
    }
    circle.setAttribute('filter', bubbleEmphasisTokens.volumeEmphasisShadow);
    return;
  }
  if (stateKey) {
    restoreVolumeBubbleShadow(circle, stateKey);
    return;
  }
  circle.removeAttribute('filter');
}

/**
 * Baseline `filter` to restore when a persistent glow is removed. A volume's own
 * resting style never carries a filter (only infra circles get one, see
 * renderBubbleView), so the emphasis shadow present here can only be the transient
 * hover glow — recording it would leave every clicked volume glowing after deselect.
 */
function restingVolumeFilter(circle: SVGCircleElement): string {
  const current = circle.getAttribute('filter') ?? '';
  return current === bubbleEmphasisTokens.volumeEmphasisShadow ? '' : current;
}

function restoreVolumeBubbleShadow(circle: SVGCircleElement, stateKey: VolumeShadowStateKey): void {
  const saved = circle.dataset[stateKey];
  if (saved == null) return;
  if (saved === '') circle.removeAttribute('filter');
  else circle.setAttribute('filter', saved);
  delete circle.dataset[stateKey];
}

function clearVolumeBubbleShadowIfDimmed(circle: SVGCircleElement, packNode?: PackNode | null): void {
  if (packNode?.data?.type !== 'volume') return;
  if (circle.dataset.sbDimFilter != null) return;
  circle.dataset.sbDimFilter = circle.getAttribute('filter') ?? '';
  circle.removeAttribute('filter');
}

const pinnedChipKinds = new Set<string>([
  bubbleEmphasisTokens.chipKindName,
  bubbleEmphasisTokens.chipKindSize,
]);

export type { PackNode, PackNodeData };

export type {
  ChipElement,
  ChipMeta,
  ChipOptions,
  ChipRenderMeta,
  VolumeLabelElement,
} from './bubbleDomTypes';

type D3BoundElement = Element & { __data__?: PackNode };

export interface BubbleEmphasisContainer extends HTMLElement {
  __bubbleChipRelayout?: () => void;
}

const mainBubbleSelector =
  '.bubbles-main > circle:not(.bubble-halo):not(.bubble-hover-rim)';
const bubblesMainSelector = '.bubbles-main';
const haloSelector = '.bubble-halo, .bubble-hover-rim';
const auxCircleSelector = '.bubble-content > g > circle';
const chipSelector = '.b-chip';
const volLabelSelector = '.b-vol-label';

const skipParentTypes = new Set(['root', 'status', 'reason']);

export function isPinnedEmphasisChip(node: { dataset: DOMStringMap }): boolean {
  return node.dataset.sbEmphasisChip === '1';
}

export function nodeMatchesHighlight(
  data: PackNodeData,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (data._isOverflow) {
    return (data._overflowChildren ?? []).some((child) =>
      nodeOrDescendantMatchesHighlight(child, hl)
    );
  }
  return nodeMatchesHighlightDirect(data, hl);
}

function nodeMatchesHighlightDirect(
  data: PackNodeData,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (hl.kind === 'status') {
    if ((data.type === 'status' || data.type === 'reason') && data.key === hl.key) {
      return true;
    }
    if (data.type === 'volume' && data.volume) {
      return data.volume.supportStatus === hl.key || data.volume.activationStatus === hl.key;
    }
    return false;
  }
  if (hl.kind === 'group') {
    if (data.name === hl.name && (!hl.type || data.type === hl.type)) {
      // Name-only group highlights must not match status/reason wrappers (e.g. sidebar
      // "Unattached" row vs inner reason bubble that shares the same label).
      if (!hl.type && (data.type === 'status' || data.type === 'reason')) {
        return false;
      }
      return true;
    }
    if (data.type === 'volume' && data.volume) {
      const ebsId = data.volume.ebsId;
      if (ebsId && hl.ebsIds?.includes(ebsId)) return true;
      if (hl.type === 'instance' && data.volume.instanceId === hl.name) return true;
    }
    return false;
  }
  if (hl.kind === 'volume') {
    return data.type === 'volume' && data.volume?.ebsId === hl.ebsId;
  }
  if (hl.kind === 'entityType') {
    // PackNodeData.type is an unconstrained string, so compare it against each
    // known HierarchyNodeType rather than widening the highlight's typed list.
    return !!data.type && hl.types.some((t) => t === data.type);
  }
  return false;
}

/** Walk a capped overflow subtree (HierarchyNode-shaped) for highlight matches. */
function nodeOrDescendantMatchesHighlight(
  data: PackNodeData,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (nodeMatchesHighlightDirect(data, hl)) return true;
  // Shallow hierarchy keeps volumes on `_volumes` with `children: null`.
  for (const vol of data._volumes ?? []) {
    if (nodeMatchesHighlightDirect({ type: 'volume', volume: vol }, hl)) return true;
  }
  for (const child of data.children ?? []) {
    if (nodeOrDescendantMatchesHighlight(child, hl)) return true;
  }
  return false;
}

export function isEmphasized(
  packNode: PackNode | null | undefined,
  hl: NonNullable<ExplorationHighlight>
): boolean {
  if (!packNode) return false;
  const chain = packNode.ancestors?.() ?? [packNode];
  return chain.some((ancestor) => nodeMatchesHighlight(ancestor.data, hl));
}

function shouldStayBright(
  packNode: PackNode | null | undefined,
  hl: NonNullable<ExplorationHighlight>,
  pinTargets: Set<PackNode>
): boolean {
  return !!packNode && (isEmphasized(packNode, hl) || pinTargets.has(packNode));
}

/** Strict ancestors of every bright node — O(bright × depth), then O(1) membership checks. */
function buildAncestorsOfBright(brightNodes: ReadonlySet<PackNode>): Set<PackNode> {
  const ancestors = new Set<PackNode>();
  for (const bright of brightNodes) {
    let p = bright.parent ?? null;
    while (p) {
      ancestors.add(p);
      p = p.parent ?? null;
    }
    // Also fold ancestors() — covers tests / nodes with incomplete parent links.
    const chain = bright.ancestors?.() ?? [];
    for (let i = 1; i < chain.length; i++) ancestors.add(chain[i]);
  }
  return ancestors;
}

/** Nearest structural ancestor (cluster, instance, nodeGroup, etc.) of a pack node. */
export function immediateStructuralParent(packNode: PackNode): PackNode | null {
  let p = packNode.parent;
  while (p) {
    const type = p.data?.type;
    if (type && !skipParentTypes.has(type)) return p;
    p = p.parent;
  }

  const chain = packNode.ancestors?.() ?? [packNode];
  for (let i = 1; i < chain.length; i++) {
    const type = chain[i].data?.type;
    if (type && !skipParentTypes.has(type)) return chain[i];
  }
  return null;
}

function relayoutChips(container: HTMLElement) {
  (container as BubbleEmphasisContainer).__bubbleChipRelayout?.();
}

function addPinTargetForMatch(pinTargets: Set<PackNode>, packNode: PackNode) {
  const dataType = packNode.data.type;
  if (dataType === 'status' || dataType === 'reason') {
    const parent = immediateStructuralParent(packNode);
    if (
      parent &&
      parent.data.name != null &&
      parent.data.name !== packNode.data.name
    ) {
      pinTargets.add(parent);
    }
  } else {
    pinTargets.add(packNode);
  }
}

function buildHierarchyChipIndex(container: HTMLElement): Map<PackNode, ChipElement[]> {
  const index = new Map<PackNode, ChipElement[]>();
  container.querySelectorAll<ChipElement>(chipSelector).forEach((chip) => {
    const meta = chip.__chipMeta;
    if (!meta?._linkedNode || !meta._chipKind || !pinnedChipKinds.has(meta._chipKind)) {
      return;
    }
    const chips = index.get(meta._linkedNode) ?? [];
    chips.push(chip);
    index.set(meta._linkedNode, chips);
  });
  return index;
}

function pinEmphasisChip(chip: ChipElement) {
  restoreDimmedChipElement(chip);
  chip.dataset.sbEmphasisChip = '1';
  chip.style.display = '';
}

function unpinAllEmphasisChips(container: HTMLElement) {
  container.querySelectorAll<ChipElement>(chipSelector).forEach((chip) => {
    if (chip.dataset.sbEmphasisChip !== '1') return;
    delete chip.dataset.sbEmphasisChip;
  });
}

function restoreDimmedCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbDim !== '1') return;
  const fill = circle.dataset.sbDimFill;
  const stroke = circle.dataset.sbDimStroke;
  const fillOpacity = circle.dataset.sbDimFillOpacity;
  const strokeOpacity = circle.dataset.sbDimStrokeOpacity;
  if (fill != null) {
    if (fill === '') circle.removeAttribute('fill');
    else circle.setAttribute('fill', fill);
  }
  if (stroke != null) {
    if (stroke === '') circle.removeAttribute('stroke');
    else circle.setAttribute('stroke', stroke);
  }
  if (fillOpacity != null) {
    if (fillOpacity === '') circle.removeAttribute('fill-opacity');
    else circle.setAttribute('fill-opacity', fillOpacity);
  }
  if (strokeOpacity != null) {
    if (strokeOpacity === '') circle.removeAttribute('stroke-opacity');
    else circle.setAttribute('stroke-opacity', strokeOpacity);
  }
  const filter = circle.dataset.sbDimFilter;
  if (filter != null) {
    if (filter === '') circle.removeAttribute('filter');
    else circle.setAttribute('filter', filter);
  }
  delete circle.dataset.sbDim;
  delete circle.dataset.sbDimFill;
  delete circle.dataset.sbDimStroke;
  delete circle.dataset.sbDimFillOpacity;
  delete circle.dataset.sbDimStrokeOpacity;
  delete circle.dataset.sbDimFilter;
  delete circle.dataset.sbDimHasHlChildren;
}

/** Emphasis-only dim restore; instance selection owns dims marked with sbInstanceDim. */
function restoreEmphasisDimmedCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbInstanceDim === '1') return;
  restoreDimmedCircle(circle);
}

function restoreHighlightedCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbHighlight !== '1') return;
  const stroke = circle.dataset.sbStroke;
  const width = circle.dataset.sbWidth;
  const opacity = circle.dataset.sbOpacity;
  const fill = circle.dataset.sbFill;
  if (stroke != null) circle.setAttribute('stroke', stroke);
  if (width != null) circle.setAttribute('stroke-width', width);
  if (opacity != null) {
    if (opacity === '') circle.removeAttribute('stroke-opacity');
    else circle.setAttribute('stroke-opacity', opacity);
  }
  if (fill != null) {
    if (fill === '') circle.removeAttribute('fill');
    else circle.setAttribute('fill', fill);
  }
  restoreVolumeBubbleShadow(circle, 'sbHighlightFilter');
  delete circle.dataset.sbHighlight;
  delete circle.dataset.sbStroke;
  delete circle.dataset.sbWidth;
  delete circle.dataset.sbOpacity;
  delete circle.dataset.sbFill;
}

function restoreVolumeSelectedCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbVolumeSelect !== '1') return;
  const stroke = circle.dataset.sbVolumeStroke;
  const width = circle.dataset.sbVolumeWidth;
  const opacity = circle.dataset.sbVolumeOpacity;
  const fill = circle.dataset.sbVolumeFill;
  if (stroke != null) circle.setAttribute('stroke', stroke);
  if (width != null) circle.setAttribute('stroke-width', width);
  if (opacity != null) {
    if (opacity === '') circle.removeAttribute('stroke-opacity');
    else circle.setAttribute('stroke-opacity', opacity);
  }
  if (fill != null) {
    if (fill === '') circle.removeAttribute('fill');
    else circle.setAttribute('fill', fill);
  }
  restoreVolumeBubbleShadow(circle, 'sbVolumeFilter');
  delete circle.dataset.sbVolumeSelect;
  delete circle.dataset.sbVolumeStroke;
  delete circle.dataset.sbVolumeWidth;
  delete circle.dataset.sbVolumeOpacity;
  delete circle.dataset.sbVolumeFill;
}

function restoreInstanceSelectedCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbInstanceSelect !== '1') return;
  const stroke = circle.dataset.sbInstanceStroke;
  const width = circle.dataset.sbInstanceWidth;
  const opacity = circle.dataset.sbInstanceOpacity;
  if (stroke != null) circle.setAttribute('stroke', stroke);
  if (width != null) circle.setAttribute('stroke-width', width);
  if (opacity != null) {
    if (opacity === '') circle.removeAttribute('stroke-opacity');
    else circle.setAttribute('stroke-opacity', opacity);
  }
  delete circle.dataset.sbInstanceSelect;
  delete circle.dataset.sbInstanceStroke;
  delete circle.dataset.sbInstanceWidth;
  delete circle.dataset.sbInstanceOpacity;
}

function restoreInstanceDimmedCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbInstanceDim !== '1') return;
  restoreDimmedCircle(circle);
  delete circle.dataset.sbInstanceDim;
}

function forEachSizeArrowPath(container: Element, fn: (path: SVGPathElement) => void) {
  container.querySelectorAll('.b-vol-size-arrow path').forEach((path) => {
    fn(path as SVGPathElement);
  });
}

function saveAndDimTextFill(container: Element) {
  container.querySelectorAll('text').forEach((text) => {
    const el = text as SVGTextElement;
    el.dataset.sbDimTextFill = readSvgTextFill(el);
    setSvgTextFill(el, s.chip.text.dim);
  });
  forEachSizeArrowPath(container, (el) => {
    el.dataset.sbDimPathFill = el.getAttribute('fill') ?? '';
    el.setAttribute('fill', s.chip.text.dim);
  });
}

function reapplyDimmedTextFill(container: Element) {
  container.querySelectorAll('text').forEach((text) => {
    setSvgTextFill(text as SVGTextElement, s.chip.text.dim);
  });
  forEachSizeArrowPath(container, (el) => {
    el.setAttribute('fill', s.chip.text.dim);
  });
}

function restoreDimmedTextFill(container: Element) {
  container.querySelectorAll('text').forEach((text) => {
    const el = text as SVGTextElement;
    const fill = el.dataset.sbDimTextFill;
    if (fill != null) {
      if (fill === '') clearSvgTextFill(el);
      else setSvgTextFill(el, fill);
    }
    delete el.dataset.sbDimTextFill;
  });
  forEachSizeArrowPath(container, (el) => {
    const fill = el.dataset.sbDimPathFill;
    if (fill != null) {
      if (fill === '') el.removeAttribute('fill');
      else el.setAttribute('fill', fill);
    }
    delete el.dataset.sbDimPathFill;
  });
}

function chipIconDimTarget(chip: ChipElement): SVGElement | null {
  return (
    (chip.querySelector('.b-chip-icon-badge') as SVGElement | null) ??
    (chip.querySelector('svg') as SVGElement | null)
  );
}

function saveAndDimIconOpacity(
  icon: SVGElement,
  stateHost: { dataset: DOMStringMap },
  dimOpacity: string
) {
  stateHost.dataset.sbDimIconOpacity = icon.getAttribute('opacity') ?? '';
  icon.setAttribute('opacity', dimOpacity);
}

function reapplyDimmedIconOpacity(icon: SVGElement, dimOpacity: string) {
  icon.setAttribute('opacity', dimOpacity);
}

function restoreDimmedIconOpacity(icon: SVGElement, stateHost: { dataset: DOMStringMap }) {
  const iconOpacity = stateHost.dataset.sbDimIconOpacity;
  if (iconOpacity != null) {
    if (iconOpacity === '') icon.removeAttribute('opacity');
    else icon.setAttribute('opacity', iconOpacity);
  }
  delete stateHost.dataset.sbDimIconOpacity;
}

function restoreDimmedInfraChip(chip: ChipElement) {
  if (chip.dataset.sbDim !== '1') return;
  const rect = chip.querySelector('rect');
  if (rect) {
    const stroke = chip.dataset.sbDimChipStroke;
    const filter = chip.dataset.sbDimChipFilter;
    const fill = chip.dataset.sbDimChipFill;
    if (stroke != null) {
      if (stroke === '') rect.removeAttribute('stroke');
      else rect.setAttribute('stroke', stroke);
    }
    if (filter != null) {
      if (filter === '') rect.removeAttribute('filter');
      else rect.setAttribute('filter', filter);
    }
    if (fill != null) {
      if (fill === '') rect.removeAttribute('fill');
      else rect.setAttribute('fill', fill);
    }
  }
  restoreDimmedTextFill(chip);
  const icon = chipIconDimTarget(chip);
  if (icon) restoreDimmedIconOpacity(icon, chip);
  delete chip.dataset.sbDim;
  delete chip.dataset.sbDimChipStroke;
  delete chip.dataset.sbDimChipFilter;
  delete chip.dataset.sbDimChipFill;
}

/** Re-applies infra chip dim attrs after zoom re-layout rebuilds chip DOM. */
export function reapplyInfraChipDimIfNeeded(chip: ChipElement): void {
  if (chip.dataset.sbDim !== '1') return;
  applyDimToInfraChip(chip);
}

/** Re-applies volume label dim attrs after zoom re-layout resets text fill. */
export function reapplyVolumeLabelDimIfNeeded(label: SVGElement): void {
  if (label.dataset.sbDim !== '1') return;
  applyDimToVolumeLabel(label);
}

function applyInfraChipDimRectVisuals(chip: ChipElement, rect: SVGRectElement) {
  rect.setAttribute('stroke', s.chip.border);
  if (rect.getAttribute('filter')?.includes('b-chip-shadow-size')) {
    rect.removeAttribute('filter');
  }
  if (chip.__chipMeta?._linkedNode?.data?.type === 'account') {
    rect.setAttribute('fill', s.chip.accountNameBgDim);
  }
}

function applyDimToInfraChip(chip: ChipElement) {
  if (chip.dataset.sbDim === '1' && chip.dataset.sbDimChipStroke != null) {
    reapplyInfraChipDimVisuals(chip);
    return;
  }
  chip.dataset.sbDim = '1';
  const rect = chip.querySelector('rect');
  if (rect) {
    chip.dataset.sbDimChipStroke = rect.getAttribute('stroke') ?? '';
    chip.dataset.sbDimChipFilter = rect.getAttribute('filter') ?? '';
    chip.dataset.sbDimChipFill = rect.getAttribute('fill') ?? '';
    applyInfraChipDimRectVisuals(chip, rect);
  }
  saveAndDimTextFill(chip);
  const icon = chipIconDimTarget(chip);
  if (icon) saveAndDimIconOpacity(icon, chip, s.icon.dimOpacity);
}

function reapplyInfraChipDimVisuals(chip: ChipElement) {
  const rect = chip.querySelector('rect');
  if (rect) applyInfraChipDimRectVisuals(chip, rect);
  reapplyDimmedTextFill(chip);
  const icon = chipIconDimTarget(chip);
  if (icon) reapplyDimmedIconOpacity(icon, s.icon.dimOpacity);
}

function dimChipElement(chip: ChipElement) {
  const linkedType = chip.__chipMeta?._linkedNode?.data?.type;
  if (isInfraHierarchyType(linkedType) || isVolumeDomainType(linkedType)) {
    applyDimToInfraChip(chip);
  } else {
    applyDimToElement(chip);
  }
}

function restoreDimmedChipElement(chip: ChipElement) {
  const linkedType = chip.__chipMeta?._linkedNode?.data?.type;
  if (isInfraHierarchyType(linkedType) || isVolumeDomainType(linkedType)) {
    restoreDimmedInfraChip(chip);
  } else {
    restoreDimmedElement(chip);
  }
}

function syncInfraDimShadows(container: HTMLElement) {
  const index = (container as BubbleInfraContainer).__infraNodeIndex;
  if (!index) return;
  index.forEach((set) => {
    const isDimmed =
      set.circle?.dataset.sbDim === '1' || set.circle?.dataset.sbInstanceDim === '1';
    // Dimmed infra keeps the soft default shadow (same as resting).
    if (isDimmed) applyInfraGroupFilter(set, s.bubble.shadowFilter.default);
  });
}

function restoreDimmedElement(el: HTMLElement | SVGElement) {
  if (el.dataset.sbDim !== '1') return;
  const opacity = el.dataset.sbDimOpacity;
  if (opacity != null) {
    if (opacity === '') el.removeAttribute('opacity');
    else el.setAttribute('opacity', opacity);
  }
  delete el.dataset.sbDim;
  delete el.dataset.sbDimOpacity;
}

type ApplyDimToCircleOptions = {
  /** Infra parent that still contains a bright/highlighted descendant. */
  hasHighlightedChildren?: boolean;
};

function applyDimToCircle(
  circle: SVGCircleElement,
  packNode?: PackNode | null,
  options: ApplyDimToCircleOptions = {}
) {
  if (circle.dataset.sbDim === '1') return;
  circle.dataset.sbDim = '1';
  circle.dataset.sbDimFill = circle.getAttribute('fill') ?? '';
  circle.dataset.sbDimStroke = circle.getAttribute('stroke') ?? '';
  circle.dataset.sbDimFillOpacity = circle.getAttribute('fill-opacity') ?? '';
  circle.dataset.sbDimStrokeOpacity = circle.getAttribute('stroke-opacity') ?? '';

  const rawFill = circle.getAttribute('fill');
  const rawStroke = circle.getAttribute('stroke');
  const volumeDim = isVolumeDomainType(packNode?.data?.type);
  const infraDim = isInfraHierarchyType(packNode?.data?.type);
  const hasHlChildren = !!options.hasHighlightedChildren;

  let dimFill = bubbleEmphasisTokens.dimmedFill;
  let dimStroke = bubbleEmphasisTokens.dimmedStroke;
  if (volumeDim) {
    dimFill = vb.volume.dim.fill;
    dimStroke = hasHlChildren
      ? s.bubble.border.dimWithHighlightedChildren
      : vb.volume.dim.stroke;
  } else if (infraDim) {
    dimStroke = hasHlChildren
      ? s.bubble.border.dimWithHighlightedChildren
      : s.bubble.border.dim;
  }

  if (volumeDim || infraDim) {
    if (hasHlChildren) circle.dataset.sbDimHasHlChildren = '1';
    else delete circle.dataset.sbDimHasHlChildren;
  }

  if (rawFill && rawFill !== 'none') {
    circle.setAttribute('fill', dimFill);
    circle.setAttribute('fill-opacity', '1');
  }

  if (rawStroke && rawStroke !== 'none') {
    circle.setAttribute('stroke', dimStroke);
    circle.setAttribute('stroke-opacity', '1');
  }

  clearVolumeBubbleShadowIfDimmed(circle, packNode);
}

function applyHighlightToCircle(circle: SVGCircleElement, packNode?: PackNode | null) {
  if (circle.dataset.sbHighlight !== '1') {
    circle.dataset.sbHighlight = '1';
    circle.dataset.sbStroke = circle.getAttribute('stroke') || '';
    circle.dataset.sbWidth = circle.getAttribute('stroke-width') || '';
    circle.dataset.sbOpacity = circle.getAttribute('stroke-opacity') ?? '';
    circle.setAttribute('stroke', bubbleEmphasisTokens.highlightStrokeResolved);
    circle.setAttribute('stroke-width', bubbleEmphasisTokens.highlightStrokeWidth);
    circle.setAttribute('stroke-opacity', '1');
  }
  if (packNode?.data.type === 'volume') {
    setVolumeBubbleShadow(circle, true, 'sbHighlightFilter');
  }
}

type VolumeRingMark = 'highlight' | 'volumeSelect';

function markDatasetKey(mark: VolumeRingMark): 'sbHighlight' | 'sbVolumeSelect' {
  return mark === 'highlight' ? 'sbHighlight' : 'sbVolumeSelect';
}

function saveVolumeRingAttrs(
  circle: SVGCircleElement,
  mark: VolumeRingMark,
  opts: { fill: boolean; view?: ViewMode; managedInner?: boolean }
) {
  const key = markDatasetKey(mark);
  if (circle.dataset[key] === '1') return false;
  circle.dataset[key] = '1';
  if (mark === 'highlight') {
    circle.dataset.sbStroke = circle.getAttribute('stroke') || '';
    circle.dataset.sbWidth = circle.getAttribute('stroke-width') || '';
    circle.dataset.sbOpacity = circle.getAttribute('stroke-opacity') ?? '';
    if (opts.fill) circle.dataset.sbFill = circle.getAttribute('fill') ?? '';
    return true;
  }

  // Selection restore must use resting tokens — never the live hover stroke
  // (hover and selection share primary-500, which left prior volumes looking selected).
  const packNode = getPackNode(circle);
  if (opts.managedInner) {
    circle.dataset.sbVolumeStroke = vb.volume.managed.innerStroke;
    circle.dataset.sbVolumeWidth = '1';
    circle.dataset.sbVolumeOpacity = '1';
    if (opts.fill) circle.dataset.sbVolumeFill = vb.volume.managed.innerFill;
  } else if (packNode && opts.view != null) {
    const datum = packNode as unknown as BubbleDatum;
    circle.dataset.sbVolumeStroke = strokeForBubble(datum, opts.view);
    circle.dataset.sbVolumeWidth = String(strokeWidthForBubble(datum));
    circle.dataset.sbVolumeOpacity = String(strokeOpacityForBubble(datum));
    if (opts.fill) circle.dataset.sbVolumeFill = fillForBubble(datum);
  } else {
    circle.dataset.sbVolumeStroke = circle.getAttribute('stroke') || '';
    circle.dataset.sbVolumeWidth = circle.getAttribute('stroke-width') || '';
    circle.dataset.sbVolumeOpacity = circle.getAttribute('stroke-opacity') ?? '';
    if (opts.fill) circle.dataset.sbVolumeFill = circle.getAttribute('fill') ?? '';
  }
  return true;
}

function applyManagedVolumeOuterEmphasis(
  circle: SVGCircleElement,
  mark: VolumeRingMark,
  view: ViewMode = 'exploration'
) {
  const applied = saveVolumeRingAttrs(circle, mark, {
    fill: true,
    view,
  });
  if (applied) {
    circle.setAttribute('fill', vb.volume.managed.emphasis.outerFill);
    circle.setAttribute('stroke', bubbleEmphasisTokens.highlightStrokeResolved);
    circle.setAttribute('stroke-width', bubbleEmphasisTokens.highlightStrokeWidth);
    circle.setAttribute('stroke-opacity', '1');
  }
  setVolumeBubbleShadow(
    circle,
    true,
    mark === 'highlight' ? 'sbHighlightFilter' : 'sbVolumeFilter'
  );
}

function applyManagedVolumeInnerEmphasis(
  circle: SVGCircleElement,
  mark: VolumeRingMark,
  view: ViewMode = 'exploration'
) {
  if (
    !saveVolumeRingAttrs(circle, mark, {
      fill: true,
      view,
      managedInner: mark === 'volumeSelect',
    })
  ) {
    return;
  }
  circle.setAttribute('fill', vb.volume.managed.emphasis.innerFill);
  circle.setAttribute('stroke', vb.volume.managed.emphasis.innerStroke);
  circle.setAttribute('stroke-width', '1');
  circle.setAttribute('stroke-opacity', '1');
}

function applyVolumeSelectionToCircle(
  circle: SVGCircleElement,
  view: ViewMode = 'exploration'
) {
  if (circle.dataset.sbVolumeSelect !== '1') {
    saveVolumeRingAttrs(circle, 'volumeSelect', { fill: false, view });
    circle.setAttribute('stroke', bubbleEmphasisTokens.highlightStrokeResolved);
    circle.setAttribute('stroke-width', bubbleEmphasisTokens.highlightStrokeWidth);
    circle.setAttribute('stroke-opacity', '1');
  }
  setVolumeBubbleShadow(circle, true, 'sbVolumeFilter');
}

function applyInstanceSelectionToCircle(circle: SVGCircleElement) {
  if (circle.dataset.sbInstanceSelect === '1') return;
  circle.dataset.sbInstanceSelect = '1';
  circle.dataset.sbInstanceStroke = circle.getAttribute('stroke') || '';
  circle.dataset.sbInstanceWidth = circle.getAttribute('stroke-width') || '';
  circle.dataset.sbInstanceOpacity = circle.getAttribute('stroke-opacity') ?? '';
  circle.setAttribute('stroke', bubbleEmphasisTokens.highlightStrokeResolved);
  circle.setAttribute('stroke-width', bubbleEmphasisTokens.highlightStrokeWidth);
  circle.setAttribute('stroke-opacity', '1');
}

function applyInstanceDimToCircle(
  circle: SVGCircleElement,
  ancestorsOfBright?: ReadonlySet<PackNode>
) {
  if (circle.dataset.sbHighlight === '1') return;
  if (circle.dataset.sbInstanceDim === '1' || circle.dataset.sbDim === '1') return;
  circle.dataset.sbInstanceDim = '1';
  const packNode = getPackNode(circle);
  applyDimToCircle(circle, packNode, {
    hasHighlightedChildren: !!packNode && !!ancestorsOfBright?.has(packNode),
  });
}

function isSelectionLockedCircle(circle: SVGCircleElement): boolean {
  return circle.dataset.sbInstanceSelect === '1' || circle.dataset.sbVolumeSelect === '1';
}

function instanceNodeMatches(packNode: PackNode | null, instanceId: string): boolean {
  return packNode?.data?.type === 'instance' && packNode.data.name === instanceId;
}

function isInsideSelectedInstance(packNode: PackNode | null, instanceId: string): boolean {
  if (!packNode) return false;
  const chain = packNode.ancestors?.() ?? [packNode];
  return chain.some((node) => instanceNodeMatches(node, instanceId));
}

function applyRestingCircleStyle(circle: SVGCircleElement, packNode: PackNode, view: ViewMode) {
  const datum = packNode as unknown as BubbleDatum;
  circle.setAttribute('stroke', strokeForBubble(datum, view));
  circle.setAttribute('stroke-width', String(strokeWidthForBubble(datum)));
  circle.setAttribute('stroke-opacity', String(strokeOpacityForBubble(datum)));
  circle.setAttribute('fill-opacity', String(fillOpacityForBubble(datum)));
  const dash = strokeDashForBubble(datum);
  if (dash) circle.setAttribute('stroke-dasharray', dash);
  else circle.removeAttribute('stroke-dasharray');
}

function restoreVolumeLabels(container: HTMLElement) {
  container.querySelectorAll<SVGElement>(volLabelSelector).forEach(restoreDimmedVolumeLabel);
}

function applyDimToVolumeLabel(label: SVGElement) {
  if (label.dataset.sbDim === '1' && label.querySelector('text')?.dataset.sbDimTextFill != null) {
    reapplyVolumeLabelDimVisuals(label);
    return;
  }
  label.dataset.sbDim = '1';
  saveAndDimTextFill(label);
  const icon = label.querySelector<SVGElement>('.b-vol-icon svg');
  if (icon) saveAndDimIconOpacity(icon, label, s.icon.dimOpacity);
}

function reapplyVolumeLabelDimVisuals(label: SVGElement) {
  reapplyDimmedTextFill(label);
  const icon = label.querySelector<SVGElement>('.b-vol-icon svg');
  if (icon) reapplyDimmedIconOpacity(icon, s.icon.dimOpacity);
}

function restoreDimmedVolumeLabel(label: SVGElement) {
  if (label.dataset.sbDim !== '1') return;
  restoreDimmedTextFill(label);
  const icon = label.querySelector<SVGElement>('.b-vol-icon svg');
  if (icon) restoreDimmedIconOpacity(icon, label);
  delete label.dataset.sbDim;
}

function applyDimToElement(el: HTMLElement | SVGElement) {
  if (el.dataset.sbDim === '1') return;
  el.dataset.sbDim = '1';
  el.dataset.sbDimOpacity = el.getAttribute('opacity') ?? '';
  el.setAttribute('opacity', String(bubbleEmphasisTokens.dimElementOpacity));
}

function getPackNode(el: D3BoundElement): PackNode | null {
  return el.__data__ ?? null;
}

function findManagedInnerCircle(
  container: HTMLElement,
  packNode: PackNode
): SVGCircleElement | null {
  let match: SVGCircleElement | null = null;
  container.querySelectorAll<SVGCircleElement>(auxCircleSelector).forEach((circle) => {
    if (getPackNode(circle) === packNode) match = circle;
  });
  return match;
}

function isManagedVolumeNode(packNode: PackNode): boolean {
  return (
    packNode.data.type === 'volume' &&
    !!packNode.data.volume &&
    isVolumeManaged(packNode.data.volume)
  );
}

function applyVolumeRingEmphasis(
  container: HTMLElement,
  packNode: PackNode,
  mark: VolumeRingMark,
  view: ViewMode = 'exploration'
) {
  const mainCircles = container.querySelectorAll<SVGCircleElement>(mainBubbleSelector);
  mainCircles.forEach((circle) => {
    if (getPackNode(circle) !== packNode) return;
    applyManagedVolumeOuterEmphasis(circle, mark, view);
  });
  const inner = findManagedInnerCircle(container, packNode);
  if (inner) applyManagedVolumeInnerEmphasis(inner, mark, view);
}

function processCircle(
  circle: SVGCircleElement,
  hl: NonNullable<ExplorationHighlight>,
  pinTargets: Set<PackNode>,
  ancestorsOfBright: ReadonlySet<PackNode>,
  view: ViewMode
) {
  const packNode = getPackNode(circle);
  if (!packNode) return;

  const emphasized = shouldStayBright(packNode, hl, pinTargets);
  const directMatch = nodeMatchesHighlight(packNode.data, hl);

  if (!emphasized) {
    if (!isSelectionLockedCircle(circle)) {
      applyDimToCircle(circle, packNode, {
        hasHighlightedChildren: ancestorsOfBright.has(packNode),
      });
    }
    return;
  }
  if (directMatch) {
    restoreInstanceDimmedCircle(circle);
    if (hl.kind === 'volume' && isManagedVolumeNode(packNode)) {
      applyManagedVolumeOuterEmphasis(circle, 'highlight');
    } else {
      applyHighlightToCircle(circle, packNode);
    }
    return;
  }
  if (pinTargets.has(packNode)) {
    applyRestingCircleStyle(circle, packNode, view);
  }
}

function pinEmphasisChips(
  container: HTMLElement,
  pinTargets: PackNode[],
  hierarchyChips: Map<PackNode, ChipElement[]>
) {
  pinTargets.forEach((node) => {
    const chips = hierarchyChips.get(node) ?? [];
    chips.forEach((chip) => pinEmphasisChip(chip));
  });
  relayoutChips(container);
}

export interface ClearBubbleEmphasisOptions {
  skipRelayout?: boolean;
}

export function clearBubbleEmphasis(
  container: HTMLElement,
  options: ClearBubbleEmphasisOptions = {}
) {
  unpinAllEmphasisChips(container);

  container.querySelectorAll<SVGCircleElement>(mainBubbleSelector).forEach((circle) => {
    restoreEmphasisDimmedCircle(circle);
    restoreHighlightedCircle(circle);
  });
  container.querySelectorAll<SVGCircleElement>(haloSelector).forEach(restoreEmphasisDimmedCircle);
  container.querySelectorAll<SVGCircleElement>(auxCircleSelector).forEach((circle) => {
    restoreEmphasisDimmedCircle(circle);
    restoreHighlightedCircle(circle);
  });
  container.querySelectorAll<ChipElement>(chipSelector).forEach(restoreDimmedChipElement);
  restoreVolumeLabels(container);

  if (!options.skipRelayout) {
    relayoutChips(container);
  }
}

export function applyBubbleEmphasis(
  container: HTMLElement,
  hl: NonNullable<ExplorationHighlight>,
  view: ViewMode = 'exploration'
) {
  const pinTargets = new Set<PackNode>();
  const mainCircles = container.querySelectorAll<SVGCircleElement>(mainBubbleSelector);

  mainCircles.forEach((circle) => {
    const packNode = getPackNode(circle);
    if (packNode && nodeMatchesHighlight(packNode.data, hl)) {
      addPinTargetForMatch(pinTargets, packNode);
    }
  });

  const brightNodes = new Set<PackNode>();
  mainCircles.forEach((circle) => {
    const packNode = getPackNode(circle);
    if (packNode && shouldStayBright(packNode, hl, pinTargets)) {
      brightNodes.add(packNode);
    }
  });
  const ancestorsOfBright = buildAncestorsOfBright(brightNodes);

  mainCircles.forEach((circle) => {
    processCircle(circle, hl, pinTargets, ancestorsOfBright, view);
  });

  container.querySelectorAll<SVGCircleElement>(haloSelector).forEach((circle) => {
    const packNode = getPackNode(circle);
    if (!packNode) return;
    if (!shouldStayBright(packNode, hl, pinTargets)) {
      applyDimToCircle(circle, packNode, {
        hasHighlightedChildren: ancestorsOfBright.has(packNode),
      });
    }
  });

  container.querySelectorAll<SVGCircleElement>(auxCircleSelector).forEach((circle) => {
    const packNode = getPackNode(circle);
    if (!packNode) return;
    const directMatch = nodeMatchesHighlight(packNode.data, hl);
    if (directMatch && hl.kind === 'volume' && isManagedVolumeNode(packNode)) {
      restoreInstanceDimmedCircle(circle);
      applyManagedVolumeInnerEmphasis(circle, 'highlight');
      return;
    }
    if (!shouldStayBright(packNode, hl, pinTargets)) {
      applyDimToCircle(circle, packNode, {
        hasHighlightedChildren: ancestorsOfBright.has(packNode),
      });
    }
  });

  const hierarchyChips = buildHierarchyChipIndex(container);

  container.querySelectorAll<ChipElement>(chipSelector).forEach((chip) => {
    const linkedNode = chip.__chipMeta?._linkedNode;
    if (!linkedNode || shouldStayBright(linkedNode, hl, pinTargets)) return;
    // Keep name/size chips readable on dimmed ancestors of the highlight.
    if (ancestorsOfBright.has(linkedNode)) return;
    dimChipElement(chip);
  });

  container.querySelectorAll<SVGElement>(volLabelSelector).forEach((label) => {
    const packNode = getPackNode(label as D3BoundElement);
    if (!packNode || shouldStayBright(packNode, hl, pinTargets)) return;
    applyDimToVolumeLabel(label);
  });

  const chipPinTargets = new Set(pinTargets);
  ancestorsOfBright.forEach((node) => chipPinTargets.add(node));
  pinEmphasisChips(container, [...chipPinTargets], hierarchyChips);
  syncInfraDimShadows(container);
}

export function clearVolumeSelection(container: HTMLElement) {
  container.querySelectorAll<SVGCircleElement>(mainBubbleSelector).forEach((circle) => {
    restoreVolumeSelectedCircle(circle);
  });
  container
    .querySelectorAll<SVGCircleElement>(auxCircleSelector)
    .forEach((circle) => restoreVolumeSelectedCircle(circle));
}

export function applyVolumeSelection(
  container: HTMLElement,
  ebsId: string,
  view: ViewMode = 'exploration'
) {
  container.querySelectorAll<SVGCircleElement>(mainBubbleSelector).forEach((circle) => {
    const packNode = getPackNode(circle);
    if (packNode?.data.type !== 'volume' || packNode.data.volume?.ebsId !== ebsId) return;
    restoreInstanceDimmedCircle(circle);
    if (isManagedVolumeNode(packNode)) {
      applyVolumeRingEmphasis(container, packNode, 'volumeSelect', view);
    } else {
      applyVolumeSelectionToCircle(circle, view);
    }
  });
}

export function clearInstanceSelection(container: HTMLElement) {
  container.querySelectorAll<SVGCircleElement>(mainBubbleSelector).forEach((circle) => {
    restoreInstanceDimmedCircle(circle);
    restoreInstanceSelectedCircle(circle);
  });
  container.querySelectorAll<SVGCircleElement>(haloSelector).forEach(restoreInstanceDimmedCircle);
  container
    .querySelectorAll<SVGCircleElement>(auxCircleSelector)
    .forEach(restoreInstanceDimmedCircle);
  container.querySelectorAll<ChipElement>(chipSelector).forEach(restoreDimmedChipElement);
  restoreVolumeLabels(container);
}

export function applyInstanceSelection(container: HTMLElement, instanceId: string) {
  const mainCircles = container.querySelectorAll<SVGCircleElement>(mainBubbleSelector);
  let hasInstanceNode = false;
  for (const circle of mainCircles) {
    if (instanceNodeMatches(getPackNode(circle), instanceId)) {
      hasInstanceNode = true;
      break;
    }
  }
  if (!hasInstanceNode) return;

  const brightUnderSelection = new Set<PackNode>();
  mainCircles.forEach((circle) => {
    const packNode = getPackNode(circle);
    if (!packNode) return;
    if (
      instanceNodeMatches(packNode, instanceId) ||
      isInsideSelectedInstance(packNode, instanceId)
    ) {
      brightUnderSelection.add(packNode);
    }
  });
  const ancestorsOfBright = buildAncestorsOfBright(brightUnderSelection);

  mainCircles.forEach((circle) => {
    const packNode = getPackNode(circle);
    if (instanceNodeMatches(packNode, instanceId)) {
      restoreInstanceDimmedCircle(circle);
      applyInstanceSelectionToCircle(circle);
      return;
    }
    if (isInsideSelectedInstance(packNode, instanceId)) {
      restoreInstanceDimmedCircle(circle);
      return;
    }
    applyInstanceDimToCircle(circle, ancestorsOfBright);
  });

  container.querySelectorAll<SVGCircleElement>(haloSelector).forEach((circle) => {
    const packNode = getPackNode(circle);
    if (instanceNodeMatches(packNode, instanceId)) return;
    if (isInsideSelectedInstance(packNode, instanceId)) {
      restoreInstanceDimmedCircle(circle);
      return;
    }
    applyInstanceDimToCircle(circle, ancestorsOfBright);
  });

  container.querySelectorAll<SVGCircleElement>(auxCircleSelector).forEach((circle) => {
    const packNode = getPackNode(circle);
    if (instanceNodeMatches(packNode, instanceId)) return;
    if (isInsideSelectedInstance(packNode, instanceId)) {
      restoreInstanceDimmedCircle(circle);
      return;
    }
    applyInstanceDimToCircle(circle, ancestorsOfBright);
  });

  container.querySelectorAll<SVGElement>(volLabelSelector).forEach((label) => {
    const packNode = getPackNode(label as D3BoundElement);
    if (!packNode) return;
    if (instanceNodeMatches(packNode, instanceId)) return;
    if (isInsideSelectedInstance(packNode, instanceId)) {
      restoreDimmedVolumeLabel(label);
      return;
    }
    applyDimToVolumeLabel(label);
  });

  container.querySelectorAll<ChipElement>(chipSelector).forEach((chip) => {
    const linkedNode = chip.__chipMeta?._linkedNode;
    if (!linkedNode) return;
    if (instanceNodeMatches(linkedNode, instanceId)) return;
    if (isInsideSelectedInstance(linkedNode, instanceId)) {
      restoreDimmedChipElement(chip);
      return;
    }
    // Keep chips on ancestors of the selected instance undimmed.
    if (ancestorsOfBright.has(linkedNode)) return;
    if (isInfraHierarchyType(linkedNode.data.type) || isVolumeDomainType(linkedNode.data.type)) {
      dimChipElement(chip);
    }
  });

  syncInfraDimShadows(container);
}

export interface CanvasOverlayOptions {
  highlight: ExplorationHighlight;
  view: ViewMode;
  selectedInstanceId: string | null;
  selectedVolumeId: string | null;
}

/** Clears and re-applies instance/volume selection plus sidebar emphasis in stable order. */
export function applyCanvasOverlays(
  container: HTMLElement,
  opts: CanvasOverlayOptions
): boolean {
  if (!container.querySelector(bubblesMainSelector)) return false;

  clearVolumeSelection(container);
  clearInstanceSelection(container);
  clearBubbleEmphasis(container, { skipRelayout: !!opts.highlight });
  if (opts.selectedInstanceId) applyInstanceSelection(container, opts.selectedInstanceId);
  if (opts.selectedVolumeId) applyVolumeSelection(container, opts.selectedVolumeId, opts.view);
  if (opts.highlight) {
    applyBubbleEmphasis(container, opts.highlight, opts.view);
    // Do not re-apply instance/volume selection here — it re-dims sidebar-highlighted
    // volume leaves (applyInstanceDimToCircle ignores sbHighlight but emphasis clears sbDim).
  }
  return true;
}
